// Copyright 2026 LanceDB Inc.
// SPDX-License-Identifier: LicenseRef-Proprietary
"use client";

import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from "react";
import { signIn, signOut } from "next-auth/react";
import { clearOidcSessionCache, redirectTo } from "@/lib/auth/oidc/session";
import { useAuthProviderName } from "@/lib/auth/providerContext";

/**
 * Serializes the two top-level navigations the OIDC provider makes, so only
 * one authentication navigation starts per document.
 *
 * Why this exists: sign-in and sign-out both hand the browser to the IdP, and
 * several things can ask for that at once. StrictMode replays effects, more
 * than one signed-out guard can mount, and clearing the local session during
 * sign-out mounts those guards while the IdP logout is still in flight. Each
 * extra signIn() starts another authorization request and overwrites the PKCE
 * cookie of the one in progress, which the callback then rejects
 * ("PKCE verification failed: Code mismatch"); a sign-in started mid-logout
 * finds the IdP session still alive and silently signs the user back in.
 */
interface OidcNavigation {
  signIn: () => Promise<void>;
  signOut: (endSessionUrl?: string) => Promise<void>;
}

const NavigationContext = createContext<OidcNavigation | null>(null);

export function OidcNavigationProvider({ children }: React.PropsWithChildren) {
  // The Auth.js provider id is the app's AUTH_PROVIDER value by contract
  // (OidcAuthOptions.providerId): "keycloak" in dash-enterprise, "oidc" in
  // the enterprise console.
  const providerId = useAuthProviderName();
  const navigating = useRef(false);

  const navigate = useCallback(async (action: () => Promise<void>) => {
    if (navigating.current) return;
    navigating.current = true;
    try {
      await action();
      // Keep ownership until the document unloads: Auth.js resolves before
      // the navigation finishes, and another sign-in would overwrite its PKCE
      // cookie.
    } catch (error) {
      navigating.current = false;
      throw error;
    }
  }, []);

  const navigation = useMemo<OidcNavigation>(
    () => ({
      signIn: () =>
        navigate(async () => {
          await signIn(providerId);
        }),
      signOut: (endSessionUrl) =>
        navigate(async () => {
          clearOidcSessionCache();
          if (endSessionUrl) {
            // Clearing the local session mounts signed-out guards. They must
            // not start a new login while the IdP session is still being
            // ended; the lock taken above is what stops them.
            await signOut({ redirect: false });
            redirectTo(endSessionUrl);
          } else {
            await signOut({ callbackUrl: "/" });
          }
        }),
    }),
    [navigate, providerId],
  );

  return (
    <NavigationContext.Provider value={navigation}>
      {children}
    </NavigationContext.Provider>
  );
}

export function useOidcNavigation(): OidcNavigation {
  const navigation = useContext(NavigationContext);
  if (!navigation) {
    throw new Error("OIDC navigation requires OidcAuthProvider");
  }
  return navigation;
}
